import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryCache } from "react-query";
import apiClient from "../utils/api-client";
import { overviewListQueryOptions } from "../utils/overview-query";
import {
  keywordMapCheckSettled,
  keywordMapJobIsActive,
  keywordMapJobStatus,
  normalizeKeywordMapPage,
  KeywordMapPage,
} from "../pages/KeywordMap/keywordMapModel";

const useKeywordMapList = ({
  projectId,
  refetchWhilePending = true,
}: {
  projectId: string;
  refetchWhilePending?: boolean;
}) => {
  const queryCache = useQueryCache();
  const cached = queryCache.getQueryData(["keywordmap", projectId]) as
    | { status?: string }[]
    | undefined;
  // React Query v2 only accepts a number or false here, not a callback.
  const stillWaiting =
    !Array.isArray(cached) ||
    cached.some((item) => !keywordMapCheckSettled(item.status));

  return useQuery(
    ["keywordmap", projectId],
    async () => {
      const res = await apiClient.getKeywordMapList({ projectId });
      const body = res?.data;
      return Array.isArray(body) ? body : [];
    },
    {
      ...overviewListQueryOptions,
      enabled: projectId.trim() !== "",
      refetchInterval: refetchWhilePending && stillWaiting ? 5000 : false,
    }
  );
};

const useDeleteKeywordMap = ({ projectId }: { projectId: string }) => {
  const queryCache = useQueryCache();
  const [deleteKeywordMap] = useMutation(
    ({
      projectId,
      keywordmapId,
    }: {
      projectId: string;
      keywordmapId: string;
    }) => {
      return apiClient.deleteKeywordMapList({ projectId, keywordmapId });
    },
    {
      onSuccess: async () => {
        await queryCache.invalidateQueries(["keywordmap", projectId]);
      },
    }
  );
  return deleteKeywordMap;
};

export type KeywordMapPageQuery = {
  searchValue: string;
  searchDropdownValue: string;
  includeExclude: string;
};

const useKeywordMapPage = ({
  projectId,
  query,
  currentPage,
  sortBy,
}: {
  projectId: string;
  query: KeywordMapPageQuery;
  currentPage: number;
  sortBy: string;
}) => {
  const queryCache = useQueryCache();
  const key = [
    "keyword-map-page",
    projectId,
    currentPage,
    query.searchValue,
    query.searchDropdownValue,
    query.includeExclude,
    sortBy,
  ];
  const cached = queryCache.getQueryData(key) as KeywordMapPage | undefined;
  const active = keywordMapJobIsActive(cached?.job?.status)
    || keywordMapJobIsActive(keywordMapJobStatus(cached?.check?.status));
  const result = useQuery(
    key,
    async () => {
      const body = await apiClient.getKeywordMap(projectId, query, currentPage, sortBy);
      return normalizeKeywordMapPage(body);
    },
    {
      enabled: projectId.trim() !== "",
      refetchInterval: active ? 4000 : false,
    }
  );
  const page = (result.data as KeywordMapPage | undefined) ?? null;
  const asked = useRef(false);

  useEffect(() => {
    if (!projectId || !page?.stale || asked.current) {
      return;
    }
    if (keywordMapJobIsActive(page.job?.status)) {
      return;
    }
    asked.current = true;
    void apiClient.refreshKeywordMap(projectId).finally(() => {
      queryCache.invalidateQueries(["keyword-map-page", projectId]);
    });
  }, [page, projectId, queryCache]);

  return {
    rows: page?.rows ?? [],
    page: page?.page ?? {},
    check: page?.check ?? null,
    job: page?.job ?? null,
    isLoading: result.status === "loading",
    reload: () => queryCache.invalidateQueries(["keyword-map-page", projectId]),
  };
};

export {
  useKeywordMapList,
  useDeleteKeywordMap,
  useKeywordMapPage,
}
